import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useClick,
  shift,
} from '@floating-ui/react';

import { useSearchBox } from 'react-instantsearch';
import { SearchIcon } from '../../shared/icons/Search';
import { SearchResultsDropdown } from './SearchResultsDropdown';
import { AlgoliaUtil, QueryParamsUtil } from '../../shared/utils';

const localizedSearchTerm = theme.locales.autocomplete.search;
const MIN_AUTOCOMPLETE_QUERY_LENGTH = 4;
const AUTOCOMPLETE_DEBOUNCE_MS = 500;

type SearchBarProps = {
  indexes: {
    product: string;
    collection: string;
    article: string;
  };
  algoliaUtil: AlgoliaUtil;
};

export const SearchBar: FC<SearchBarProps> = ({ indexes, algoliaUtil }) => {
  const { refine } = useSearchBox();

  const [searchQuery, setSearchQuery] = useState<string>(
    () => new QueryParamsUtil(window.location.search).decodeSearchQuery() ?? ''
  );

  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const lastRefinedQueryRef = useRef('');
  // The dropdown should respond to what the user actively types in the header
  // box, not to a query pre-filled from the URL on mount (e.g. on the search
  // results page, where auto-opening it would cover the page). Stay closed
  // until the user edits the input.
  const hasUserTypedRef = useRef(false);

  useEffect(() => {
    if (!hasUserTypedRef.current) {
      return;
    }

    const normalizedQuery = searchQuery.trim();

    if (normalizedQuery.length < MIN_AUTOCOMPLETE_QUERY_LENGTH) {
      setSearchResultsOpen(false);

      return;
    }

    const debounceTimer = window.setTimeout(() => {
      if (lastRefinedQueryRef.current === normalizedQuery) {
        setSearchResultsOpen(true);

        return;
      }

      refine(normalizedQuery);
      lastRefinedQueryRef.current = normalizedQuery;
      setSearchResultsOpen(true);
    }, AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [refine, searchQuery]);

  // Manage the results modal container

  const {
    refs: searchResultsRefs,
    floatingStyles: searchResultsFloatingStyles,
    context: searchResultsContext,
  } = useFloating({
    placement: 'bottom',
    open: searchResultsOpen,
    onOpenChange: (open) => {
      setSearchResultsOpen(
        hasUserTypedRef.current && searchQuery.trim().length >= MIN_AUTOCOMPLETE_QUERY_LENGTH && open
      );
    },
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), shift()],
    nodeId: 'topBarSearchResults',
  });

  const { getReferenceProps: getSearchResultsReferenceProps, getFloatingProps: getSearchResultsFloatingProps } =
    useInteractions([
      useClick(searchResultsContext, { ignoreMouse: false, toggle: true }),
      useRole(searchResultsContext, { role: 'combobox' }),
      useDismiss(searchResultsContext),
    ]);

  const newSearchResultsFloatingStyles = useMemo(() => {
    const match = searchResultsFloatingStyles.transform?.match(/^translate\((.+px), (.+px)\)$/);

    if (match && match?.length >= 2) {
      searchResultsFloatingStyles.transform = `translate(0px, ${match[2]})`;
    }

    return searchResultsFloatingStyles;
  }, [searchResultsFloatingStyles]);

  return (
    <>
      <form
        action="/search"
        method="get"
        role="search"
        className="search"
        // onSubmit={(event) => event.preventDefault()}
      >
        <label className="field__label hidden" htmlFor="Search-In-Modal">
          {localizedSearchTerm}
        </label>
        <input
          {...getSearchResultsReferenceProps()}
          ref={searchResultsRefs.setReference}
          className="search__input"
          id="Search-In-Modal"
          type="search"
          name="q"
          value={searchQuery}
          placeholder={localizedSearchTerm}
          onInput={(e) => {
            const { value } = e.currentTarget;

            hasUserTypedRef.current = true;
            setSearchQuery(value);
          }}
        />
        <input type="hidden" name="view" value="results" />
        <button className="search__button focus-inset" aria-label={localizedSearchTerm}>
          <SearchIcon size="sm" />
        </button>
      </form>
      {searchResultsOpen && (
        <FloatingPortal>
          <div
            {...getSearchResultsFloatingProps()}
            ref={searchResultsRefs.setFloating}
            style={newSearchResultsFloatingStyles}
            className="algolia-autocomplete"
          >
            <SearchResultsDropdown
              product={indexes.product}
              article={indexes.article}
              collection={indexes.collection}
              algoliaUtil={algoliaUtil}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
